import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Wordmark } from "@/components/site/Wordmark";
import { EASE } from "@/components/site/motion";
import { LINKS } from "@/content/site";

const NAV = [
  { to: "/", label: "Mission", end: true },
  { to: "/manifesto", label: "Manifesto" },
  { to: "/logbook", label: "Logbook" },
  { to: "/careers", label: "Careers" },
  { to: "/partners", label: "Partners" },
];

const useScrollState = () => {
  const [state, setState] = useState({ scrolled: false, hidden: false });
  useEffect(() => {
    let last = window.scrollY;
    const on = () => {
      const y = window.scrollY;
      setState({ scrolled: y > 24, hidden: y > last && y > 480 });
      last = y;
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return state;
};

const MobileMenu = ({ onClose }) => (
  <motion.div
    className="fixed inset-0 z-40 flex flex-col bg-ink px-5 pb-8 pt-28 md:hidden"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease: EASE }}
    data-testid="mobile-menu"
  >
    <nav className="flex flex-col gap-1">
      {NAV.map((item, i) => (
        <motion.div key={item.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.6, ease: EASE }}>
          <NavLink to={item.to} end={item.end} onClick={onClose} className={({ isActive }) => `flex items-baseline justify-between border-b border-white/10 py-4 text-4xl font-[520] tracking-[-0.03em] ${isActive ? "text-fg" : "text-fg-2"}`} data-testid={`mobile-nav-${item.label.toLowerCase()}`}>
            {item.label}
            <span className="eyebrow text-fg-3">0{i + 1}</span>
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto space-y-4">
      <Link to="/careers" onClick={onClose} className="flex w-full items-center justify-center rounded-full bg-fg py-4 text-base font-[560] text-ink" data-testid="mobile-nav-join">Join us</Link>
      <a href={LINKS.thinkingships} target="_blank" rel="noopener noreferrer" className="block text-center text-sm text-fg-3" data-testid="mobile-nav-thinkingships">Operating ships today? ThinkingShips →</a>
    </div>
  </motion.div>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const { scrolled, hidden } = useScrollState();
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[transform,background-color] duration-500 ${hidden && !open ? "-translate-y-full" : "translate-y-0"} ${scrolled && !open ? "bg-ink/70 backdrop-blur-xl border-b border-white/[0.06]" : "bg-transparent"}`}
        data-testid="site-header"
      >
        <div className="shell flex h-[68px] items-center justify-between">
          <Link to="/" className="relative z-50 text-fg" data-testid="nav-logo" aria-label="x1025 home">
            <Wordmark />
          </Link>
          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `relative rounded-full px-4 py-2 text-[14px] font-[500] transition-colors ${isActive ? "text-fg" : "text-fg-2 hover:text-fg"}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && <motion.span layoutId="nav-dot" className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-signal" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/careers" className="hidden rounded-full bg-fg px-5 py-2.5 text-[14px] font-[560] text-ink transition-colors hover:bg-signal md:inline-flex" data-testid="nav-join-cta">
              Join us
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="relative z-50 flex h-11 w-11 flex-col items-center justify-center gap-1.5 rounded-full border border-white/15 md:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
              <span className={`h-px w-5 bg-fg transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
              <span className={`h-px w-5 bg-fg transition-transform duration-300 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
